import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import AddToWatchlist from '../components/AddToWatchlist';
import CashEntryModal from '../components/CashEntryModal';
import { WatchIcon, BellIcon } from '../components/Icons';

// Money market holdings keyed by account + symbol. Their yield is shown as hover
// text on the position's row rather than as a column.
const mmKey = (accountId, symbol) => `${accountId}:${symbol}`;
const mmTitle = (m, fmt) => [
  `7-day yield ${m.rate_pct.toFixed(2)}% (as of ${m.rate_date})`,
  m.estimated_annual_income != null ? `Estimated annual income ${fmt(m.estimated_annual_income)}` : null,
  `Accrued since ${m.accrued_since} (estimate) ${fmt(m.accrued_estimate)}`,
].filter(Boolean).join('\n');

export default function Positions({ navParams = {}, onNavigate }) {
  const [open, setOpen] = useState([]);
  const [closed, setClosed] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [prices, setPrices] = useState({});
  const [tab, setTab] = useState('open');
  const [filterAccount, setFilterAccount] = useState(() => navParams.accountId ? String(navParams.accountId) : '');
  const [sortField, setSortField] = useState('symbol');
  const [sortDir, setSortDir] = useState('asc');
  const [watchSymbol, setWatchSymbol] = useState(null);
  const [moneyMarket, setMoneyMarket] = useState({});
  // Missing money market dividends: the holding whose flagged months are being
  // reviewed, and the month a dividend is being recorded for.
  const [dividendHolding, setDividendHolding] = useState(null);
  const [dividendMonth, setDividendMonth] = useState(null);

  const load = async () => {
    const [o, c, a] = await Promise.all([
      api.getPositions(filterAccount || undefined),
      api.getClosedPositions(filterAccount || undefined),
      api.getAccounts()
    ]);
    setOpen(o); setClosed(c); setAccounts(a);
    api.getMoneyMarketHoldings()
      .then(rows => setMoneyMarket(Object.fromEntries((rows || []).map(m => [mmKey(m.account_id, m.symbol), m]))))
      .catch(() => {});
    const priceMap = {};
    await Promise.all(o.map(async p => {
      try { priceMap[p.symbol] = (await api.getLatestPrice(p.symbol)).close_price; } catch {}
    }));
    setPrices(priceMap);
  };

  useEffect(() => { load(); }, [filterAccount]);

  const dismissDividend = async (m, d) => {
    if (!window.confirm(`Stop flagging ${m.symbol} for ${d.month}? Use this when no dividend was paid that month.`)) return;
    try {
      const rows = await api.dismissDividendFlag({ account_id: m.account_id, symbol: m.symbol, month: d.month });
      const map = Object.fromEntries((rows || []).map(r => [mmKey(r.account_id, r.symbol), r]));
      setMoneyMarket(map);
      const updated = map[mmKey(m.account_id, m.symbol)];
      setDividendHolding(updated && updated.missing_dividends.length > 0 ? updated : null);
    } catch (e) {
      window.alert(e.message);
    }
  };

  const fmt = (n) => n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtPct = (n) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
  const accountName = (id) => accounts.find(a => a.id === id)?.name || id;

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortTh = ({ field, label }) => (
    <th onClick={() => toggleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const sortRows = (rows) => [...rows].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (av == null) av = sortDir === 'asc' ? Infinity : -Infinity;
    if (bv == null) bv = sortDir === 'asc' ? Infinity : -Infinity;
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const enrichedOpen = open.map(p => {
    const cur = prices[p.symbol];
    const value = cur ? cur * p.quantity : null;
    const gain = value != null ? value - p.total_cost : null;
    const pct = gain != null && p.total_cost > 0 ? (gain / p.total_cost) * 100 : null;
    return { ...p, cur, value, gain, pct, mm: moneyMarket[mmKey(p.account_id, p.symbol)] };
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Positions</h1>
        <select style={{ width: 'auto' }} value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
          <option value="">All Accounts</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button className={tab === 'open' ? 'btn' : 'btn-ghost'} onClick={() => setTab('open')}>Open ({open.length})</button>
        <button className={tab === 'closed' ? 'btn' : 'btn-ghost'} onClick={() => setTab('closed')}>Closed ({closed.length})</button>
      </div>

      {tab === 'open' && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <SortTh field="account_id" label="Account" />
                <SortTh field="symbol" label="Symbol" />
                <SortTh field="quantity" label="Qty" />
                <SortTh field="avg_cost" label="Avg Cost" />
                <SortTh field="cur" label="Current" />
                <SortTh field="value" label="Value" />
                <SortTh field="gain" label="Gain" />
                <SortTh field="pct" label="Return" />
              </tr>
            </thead>
            <tbody>
              {sortRows(enrichedOpen).map((p, i) => (
                <tr
                  key={i}
                  onClick={() => onNavigate && onNavigate('transactions', { symbol: p.symbol })}
                  style={{ cursor: 'pointer' }}
                  title={p.mm && p.mm.rate_pct != null ? mmTitle(p.mm, fmt) : undefined}
                >
                  <td className="muted">{accountName(p.account_id)}</td>
                  <td>
                    <strong className="mono" style={{ color: 'var(--accent)' }}>{p.symbol}</strong>
                    {p.mm && p.mm.missing_dividends.length > 0 && (
                      <span
                        className="loss"
                        style={{ fontSize: 12, marginLeft: 8, textDecoration: 'underline' }}
                        title={`No dividend recorded for: ${p.mm.missing_dividends.map(d => d.month).join(', ')}. Click to record or dismiss.`}
                        onClick={e => { e.stopPropagation(); setDividendHolding(p.mm); }}
                      >
                        Dividend missing
                      </span>
                    )}
                  </td>
                  <td className="mono amount">{p.quantity}</td>
                  <td className="mono amount">{fmt(p.avg_cost)}</td>
                  <td className="mono amount">{p.cur ? fmt(p.cur) : <span className="muted">—</span>}</td>
                  <td className="mono amount">{p.value ? fmt(p.value) : <span className="muted">—</span>}</td>
                  <td className={`mono amount ${p.gain >= 0 ? 'gain' : 'loss'}`}>{p.gain != null ? fmt(p.gain) : '—'}</td>
                  <td className={`mono amount ${p.pct >= 0 ? 'gain' : 'loss'}`}>{p.pct != null ? fmtPct(p.pct) : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="icon-btn-sm" style={{ marginRight: 4 }} title="Watch" aria-label="Watch" onClick={e => { e.stopPropagation(); setWatchSymbol(p.symbol); }}><WatchIcon /></button>
                    <button className="icon-btn-sm" title="Alert" aria-label="Alert" onClick={e => { e.stopPropagation(); onNavigate && onNavigate('alerts', { symbol: p.symbol }); }}><BellIcon /></button>
                  </td>
                </tr>
              ))}
              {open.length === 0 && <tr><td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 32 }}>No open positions.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'closed' && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <SortTh field="account_id" label="Account" />
                <SortTh field="symbol" label="Symbol" />
                <SortTh field="quantity_sold" label="Qty Sold" />
                <SortTh field="quantity_remaining" label="Qty Remaining" />
                <SortTh field="cost_basis" label="Cost Basis" />
                <SortTh field="proceeds" label="Proceeds" />
                <SortTh field="realized_gain" label="Realized Gain" />
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sortRows(closed).map((p, i) => (
                <tr key={i} onClick={() => onNavigate && onNavigate('transactions', { symbol: p.symbol })} style={{ cursor: 'pointer' }}>
                  <td className="muted">{accountName(p.account_id)}</td>
                  <td><strong className="mono" style={{ color: 'var(--accent)' }}>{p.symbol}</strong></td>
                  <td className="mono amount">{p.quantity_sold}</td>
                  <td className="mono amount">{p.quantity_remaining}</td>
                  <td className="mono amount">{fmt(p.cost_basis)}</td>
                  <td className="mono amount">{fmt(p.proceeds)}</td>
                  <td className={`mono amount ${p.realized_gain >= 0 ? 'gain' : 'loss'}`}>{fmt(p.realized_gain)}</td>
                  <td>
                    <span className={p.status === 'Closed' ? 'muted' : 'gain'} style={{ fontSize: 12 }}>{p.status}</span>
                    {p.unassigned_quantity > 0 && (
                      <span
                        className="loss"
                        style={{ fontSize: 12, marginLeft: 8 }}
                        title="A sell has no lots selected, so its cost basis is provisional (oldest lots first). Click to assign lots."
                        onClick={e => { e.stopPropagation(); onNavigate && onNavigate('transactions', { symbol: p.symbol, transaction_type: 'sell' }); }}
                      >
                        Lots needed
                      </span>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="icon-btn-sm" style={{ marginRight: 4 }} title="Watch" aria-label="Watch" onClick={e => { e.stopPropagation(); setWatchSymbol(p.symbol); }}><WatchIcon /></button>
                    <button className="icon-btn-sm" title="Alert" aria-label="Alert" onClick={e => { e.stopPropagation(); onNavigate && onNavigate('alerts', { symbol: p.symbol }); }}><BellIcon /></button>
                  </td>
                </tr>
              ))}
              {closed.length === 0 && <tr><td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 32 }}>No closed or partial positions.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {dividendHolding && !dividendMonth && (
        <div className="modal-overlay" onClick={() => setDividendHolding(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title"><span className="mono">{dividendHolding.symbol}</span> Dividends Not Recorded</div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              {accountName(dividendHolding.account_id)} held {dividendHolding.symbol} in these months, but no dividend entry was found. Amounts are estimates from the daily 7-day yield.
            </div>
            <table>
              <tbody>
                {dividendHolding.missing_dividends.map(d => (
                  <tr key={d.month}>
                    <td className="mono">{d.month}</td>
                    <td className="mono amount">{fmt(d.estimated)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 10px', marginRight: 6 }} onClick={() => setDividendMonth(d)}>Record dividend</button>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => dismissDividend(dividendHolding, d)}>Dismiss</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setDividendHolding(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {dividendHolding && dividendMonth && (
        <CashEntryModal
          entry={{
            account_id: dividendHolding.account_id,
            type: 'dividend',
            symbol: dividendHolding.symbol,
            amount: dividendMonth.estimated || '',
            date: dividendMonth.suggested_date,
          }}
          accounts={accounts}
          lockAccount
          hint={`Prefilled with the estimate for ${dividendMonth.month} from the daily 7-day yield. Replace the amount and date with the actual figures from your statement. If the dividend was reinvested, also record a buy of ${dividendHolding.symbol} for the same amount.`}
          onClose={() => setDividendMonth(null)}
          onSaved={() => { setDividendMonth(null); setDividendHolding(null); load(); }}
        />
      )}
      {watchSymbol && <AddToWatchlist symbol={watchSymbol} onClose={() => setWatchSymbol(null)} />}
    </div>
  );
}